import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, FileText, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AttachmentImage {
  id: string;
  optimizedUrl?: string | null;
  thumbnailUrl?: string | null;
  originalUrl?: string | null;
  originalWidth?: number | null;
  originalHeight?: number | null;
}

interface AttachmentFile {
  url: string;
  name: string;
  mediaType?: string | null;
  size?: number | null;
}

interface MessageAttachment {
  id: string;
  position?: number | null;
  image?: AttachmentImage | null;
  file?: AttachmentFile | null;
}

/** "3.2 MB" / "812 KB" — compact, for the file card. */
function formatBytes(bytes?: number | null): string {
  if (bytes == null || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/**
 * Renders the attachments of a DM message part: image thumbnails and file cards.
 *
 * Images use GraphQL-signed optimized/thumbnail URLs (private bucket) and open
 * in an in-app lightbox on click (X / backdrop / Escape to dismiss) — never a
 * raw-URL navigation. Files (PDF, docs) render a card that downloads the signed
 * private URL in a new tab. A "processing" placeholder shows while an image's
 * variants are still being generated.
 */
export function MessagePartImages({ attachments, isSelf }: { attachments: MessageAttachment[]; isSelf?: boolean }) {
  const { t } = useTranslation('social');
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const closeLightbox = useCallback(() => setLightboxSrc(null), []);

  useEffect(() => {
    if (!lightboxSrc) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setLightboxSrc(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [lightboxSrc]);

  const items = [...(attachments ?? [])]
    .filter((a) => a.image || a.file)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  if (items.length === 0) return null;

  return (
    <>
      <div className={`flex flex-wrap gap-1.5 mb-1 ${isSelf ? 'justify-end' : 'justify-start'}`}>
        {items.map((a) => {
          if (a.file) {
            return (
              <a
                key={a.id}
                href={a.file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 max-w-[220px] rounded-lg border border-black/10 bg-gray-50 px-3 py-2 hover:bg-gray-100 transition-colors"
              >
                <FileText className="size-5 shrink-0 text-gray-500" />
                <span className="min-w-0 flex flex-col">
                  <span className="text-xs font-medium text-gray-800 truncate">{a.file.name}</span>
                  {formatBytes(a.file.size) && (
                    <span className="text-[10px] text-gray-500">{formatBytes(a.file.size)}</span>
                  )}
                </span>
                <Download className="size-4 shrink-0 text-gray-400" />
              </a>
            );
          }
          const src = a.image?.optimizedUrl || a.image?.thumbnailUrl || '';
          if (!src) {
            return (
              <div
                key={a.id}
                className="w-[140px] h-[140px] rounded-lg border border-gray-200 bg-gray-100 flex items-center justify-center text-[11px] text-gray-400"
              >
                {t('dm.attachments.processing', 'Processing…')}
              </div>
            );
          }
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => setLightboxSrc(src)}
              aria-label={t('dm.attachments.view', 'View image')}
              className="block cursor-zoom-in"
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="max-w-[180px] max-h-[180px] rounded-lg object-cover border border-black/5"
              />
            </button>
          );
        })}
      </div>

      {/* Portaled: the dock's `fixed z-40` wrapper is a stacking context that would
          otherwise trap this overlay under the z-50 site header. */}
      {lightboxSrc &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4"
            onClick={closeLightbox}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={closeLightbox}
              aria-label={t('common.close', 'Close')}
              className="absolute top-4 right-4 text-white bg-black/40 hover:bg-black/60 rounded-full p-1.5 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={lightboxSrc}
              alt=""
              className="max-w-full max-h-full rounded-lg object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body
        )}
    </>
  );
}
